// frontend/src/components/UsageOverview.js

import React, { useState, useEffect } from 'react';
//import axios from 'axios';
import UsageCharts from './UsageCharts'; // Import the UsageCharts component
import { Download, RefreshCw, Phone, MessageSquare, Mic, DollarSign } from 'lucide-react';
import api from '../api';

const planDetails = {
  name: 'Pro Plan',
  monthlyCallLimit: 100,
  currentCalls: 75
};

const formatNumber = (num) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num;
};

// Custom tooltips remain the same
/*const CallTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-violet-600">{payload[0].value} calls</p>
        <p className="text-green-600">${payload[1].value.toFixed(2)}</p>
      </div>
    );
  }
  return null;
};*/

/*const UsageTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-blue-600">Limit: {planDetails.monthlyCallLimit} calls</p>
        <p className="text-violet-600">Used: {planDetails.currentCalls} calls</p>
        <p className="text-gray-600">
          {Math.round((planDetails.currentCalls / planDetails.monthlyCallLimit) * 100)}% utilized
        </p>
      </div>
    );
  }
  return null;
};*/

/*const TokenTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
        <p className="font-medium text-gray-900">{label}</p>
        <p className="text-blue-600">{formatNumber(payload[0].value)} tokens</p>
      </div>
    );
  }
  return null;
};*/

export default function UsageOverview() {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dailyData, setDailyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [totalCost, setTotalCost] = useState(0);
  const [textTokens, setTextTokens] = useState(0);
  const [audioTokens, setAudioTokens] = useState(0);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUsageData();
    setIsRefreshing(false);
  };

  const fetchUsageData = async () => {
    try {
      const billingStart = '2024-12-01';
      const billingEnd = '2024-12-30';

      // Fetch daily usage
      const dailyResponse = await api.get(`${process.env.REACT_APP_API_BASE_URL}/usage/daily?billingStart=${billingStart}&billingEnd=${billingEnd}`);
      const rawDailyUsage = dailyResponse.data.dailyUsage;

      // Fetch monthly usage
      const monthlyResponse = await api.get(`${process.env.REACT_APP_API_BASE_URL}/usage/monthly?billingStart=${billingStart}&billingEnd=${billingEnd}`);
      const rawMonthlyUsage = monthlyResponse.data.monthlyUsage;

      // Fetch all usage records to calculate totals
      const usageResponse = await api.get(`${process.env.REACT_APP_API_BASE_URL}/usage?billingStart=${billingStart}&billingEnd=${billingEnd}`);

      const usageRecords = usageResponse.data.usageRecords;

      // Calculate Total Cost
      const total = usageRecords.reduce((sum, record) => sum + record.cost, 0);
      setTotalCost(total);

      // Calculate Text Tokens
      const totalText = usageRecords
        .filter(record => record.apiType === 'OpenAI_Realtime' || record.apiType === 'OpenAI_GPT4o_mini')
        .reduce((sum, record) => sum + record.inputTokens + record.outputTokens, 0);
      setTextTokens(totalText);

      // Calculate Audio Tokens
      const totalAudio = usageRecords
        .filter(record => record.apiType === 'Twilio_TTS')
        .reduce((sum, record) => sum + record.characters, 0);
      setAudioTokens(totalAudio);

      // Map Daily Usage Data
      const dailyCallData = [];
      //const dailyCostMap = {};

      rawDailyUsage.forEach(record => {
        const date = record.date.slice(0, 10); // Format date as 'YYYY-MM-DD'
        let dayRecord = dailyCallData.find(item => item.date === date);
        if (!dayRecord) {
          dayRecord = {
            date,
            totalCalls: 0,
            totalCost: 0,
            textTokens: 0,
            audioTokens: 0,
          };
          dailyCallData.push(dayRecord);
        }
        if (record.apiType === 'OpenAI_Realtime' || record.apiType === 'OpenAI_GPT4o_mini') {
          dayRecord.totalCalls += record.callCount;
          dayRecord.textTokens += record.totalInputTokens + record.totalOutputTokens;
        } else if (record.apiType === 'Twilio_TTS') {
          dayRecord.audioTokens += record.totalCharacters;
        }
        dayRecord.totalCost += record.totalCost;
      });
      

      setDailyData(dailyCallData);

      // Map Monthly Usage Data
      const monthlyUsageData = [];
      rawMonthlyUsage.forEach(record => {
        const billingPeriod = `${record.month}/${record.year}`;
        const existing = monthlyUsageData.find(item => item.billingPeriod === billingPeriod);
        if (existing) {
          existing.used += (record.totalInputTokens + record.totalOutputTokens);
        } else {
          monthlyUsageData.push({
            billingPeriod,
            used: (record.totalInputTokens + record.totalOutputTokens),
            limit: planDetails.monthlyCallLimit
          });
        }
      });

      setMonthlyData(monthlyUsageData);

    } catch (error) {
      console.error('Error fetching usage data:', error);
    }
  };

  useEffect(() => {
    fetchUsageData();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Stats Cards Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
                Usage Overview
              </h1>
              <p className="mt-2 text-sm text-gray-600">
                {planDetails.name} ({planDetails.monthlyCallLimit} calls/month)
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium text-white bg-violet-600 hover:bg-violet-700"
                disabled={isRefreshing}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
              
              <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                <Download className="w-4 h-4 mr-2" />
                Export Data
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Total Calls */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-violet-50 rounded-lg">
                  <Phone className="w-5 h-5 text-violet-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">
                    Total Calls
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {planDetails.currentCalls}/{planDetails.monthlyCallLimit}
                  </p>
                </div>
              </div>
            </div>

            {/* Text Tokens */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <MessageSquare className="w-5 h-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">
                    Text Tokens
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatNumber(textTokens)}
                  </p>
                </div>
              </div>
            </div>

            {/* Audio Tokens */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Mic className="w-5 h-5 text-green-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">
                    Audio Tokens
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatNumber(audioTokens)}
                  </p>
                </div>
              </div>
            </div>

            {/* Total Cost */}
            <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <div className="flex items-center">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <DollarSign className="w-5 h-5 text-amber-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-600">
                    Total Cost
                  </p>
                  <p className="text-lg font-semibold text-gray-900">
                    ${totalCost.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Daily Call Volume & Cost Chart */}
          <UsageCharts
            data={dailyData}
            title="Daily Call Volume & Cost"
            dataKeys={['totalCalls', 'totalCost']}
            colors={['#8884d8', '#82ca9d']}
            xAxisKey="date"
          />

          {/* Monthly Usage vs Limit Chart */}
          <UsageCharts
            data={monthlyData}
            title="Monthly Usage vs Limit"
            dataKeys={['used', 'limit']}
            colors={['#8884d8', '#e0e7ff']}
            xAxisKey="billingPeriod"
          />

          {/* Daily Text Token Usage Chart */}
          <UsageCharts
            data={dailyData}
            title="Daily Text Token Usage"
            dataKeys={['textTokens']}
            colors={['#60a5fa']}
            xAxisKey="date"
          />

          {/* Daily Audio Token Usage Chart */}
          <UsageCharts
            data={dailyData}
            title="Daily Audio Token Usage"
            dataKeys={['audioTokens']}
            colors={['#34d399']}
            xAxisKey="date"
          />
        </div>
      </div>
    </div>
  );
}
